"use client";
import { useState, useEffect, useMemo } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "../../@/components/ui/dialog";
import { Button } from "../../@/components/ui/button";
import { Input } from "../../@/components/ui/input";
import { Form, FormField } from "@/components/ui/form";
import {
  Field,
  FieldContent,
  FieldLabel,
  FieldError,
} from "../../@/components/ui/field";
import { Textarea } from "../../@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../@/components/ui/select";
import { Pencil, Info, UploadCloud, Trash2 } from "lucide-react";
import MarkdownEditorDialog from "../components/MarkdownEditorDialog";
import { toast } from "sonner";
import { api_endpoint } from "@/constants/constant";
import { useAuth, useUser } from "@clerk/clerk-react";
import axios from "axios";
import { fetchAllPaginatedItems } from "@/lib/fetchAllPaginatedItems";
import { ClipLoader } from "react-spinners";
import { useR2Upload } from "@/lib/useR2Upload";
import { cn } from "@/lib/utils";

const editCourseSchema = z.object({
  title: z.string().min(1),
  price: z.string().min(1),
  slug: z
    .string()
    .min(1)
    .max(10, "Slug must be at most 10 characters")
    .regex(/^\S+$/, "Course slug cannot contain spaces"),
  summary: z
    .string()
    .min(1)
    .refine((val) => val.trim().split(/\s+/).length <= 50, {
      message: "Summary must be at most 50 words",
    }),
  description: z.string().min(1),
  // allow either a URL string or a File / anything optional so selecting a file doesn't break zod validation
  thumbnail: z.any().optional().nullable(),
  pathways: z.array(z.string()).optional(),
  instructors: z.array(z.string()).optional(),
  managers: z.array(z.string()).optional(),
  admins: z.array(z.string()).optional(),
  difficulty: z.enum(["Beginner", "Intermediate", "Advanced"]),
  status: z.enum(["Draft", "Published"]),
  isOpen: z.enum(["0", "1"]),
});

const DIFFICULTY_REVERSE_MAP = {
  1: "Beginner",
  2: "Intermediate",
  3: "Advanced",
};

const STATUS_REVERSE_MAP = {
  0: "Draft",
  1: "Published",
};

function formatPrice(price) {
  if (!price) return "";
  return `KES ${Number(price).toLocaleString()}`;
}

function parsePrice(formattedPrice) {
  if (!formattedPrice) return "";
  return formattedPrice.replace(/[^\d]/g, ""); // removes KES and commas
}

export function EditCourseDialog({
  course,
  onCourseUpdated,
  className,
  refreshKey,
  setRefreshKey,
}) {
  const [open, setOpen] = useState(false);
  const [pathways, setPathways] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [isLoadingPathways, setIsLoadingPathways] = useState(false);
  const [loadingInstructors, setLoadingInstructors] = useState(false);
  const [managers, setManagers] = useState([]);
  const [loadingManagers, setLoadingManagers] = useState(false);
  const [markdownEditorOpen, setMarkdownEditorOpen] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [loadingAdmins, setLoadingAdmins] = useState(false);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [associatedPathwayIds, setAssociatedPathwayIds] = useState([]);
  const [associatedInstructorIds, setAssociatedInstructorIds] = useState([]);
  const [associatedManagerIds, setAssociatedManagerIds] = useState([]);
  const allUserDataLoaded =
    !isLoadingPathways &&
    !loadingInstructors &&
    !loadingManagers &&
    !loadingAdmins &&
    pathways.length > 0 &&
    instructors.length > 0 &&
    managers.length > 0 &&
    admins.length > 0 &&
    associatedPathwayIds.length >= 0 &&
    associatedInstructorIds.length >= 0 &&
    associatedManagerIds.length >= 0;

  useEffect(() => {
    // Fetch pathways
    const fetchPathways = async () => {
      setIsLoadingPathways(true);
      try {
        const response = await axios.get(api_endpoint + "/pathway/");
        setPathways(response.data?.pathways || []);
      } catch {
        setPathways([]);
      } finally {
        setIsLoadingPathways(false);
      }
    };

    // Fetch instructors
    const fetchInstructors = async () => {
      setLoadingInstructors(true);
      try {
        const token = await getToken();
        const reqConfig = {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        };
        const instructors = await fetchAllPaginatedItems({
          url: api_endpoint + `/user/users/role/${"Instructor"}`,
          token,
          selectItems: (data) => data?.users ?? [],
          mode: "skip",
          pageSize: 200,
          headers: reqConfig.headers,
        });
        setInstructors(instructors || []);
      } catch {
        setInstructors([]);
      } finally {
        setLoadingInstructors(false);
      }
    };

    // Fetch managers
    const fetchManagers = async () => {
      try {
        setLoadingManagers(true);
        const token = await getToken();
        if (!token) throw new Error("No auth token found");

        const reqConfig = {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        };
        const managers = await fetchAllPaginatedItems({
          url: api_endpoint + `/user/users/role/${"Manager"}`,
          token,
          selectItems: (data) => data?.users ?? [],
          mode: "skip",
          pageSize: 200,
          headers: reqConfig.headers,
        });
        setManagers(managers || []);
      } catch (error) {
        console.error("Failed to fetch Managers:", error);
        setManagers([]);
      } finally {
        setLoadingManagers(false);
      }
    };

    // Fetch admins
    const fetchAdmins = async () => {
      try {
        setLoadingAdmins(true);
        const token = await getToken();
        if (!token) throw new Error("No auth token found");

        const reqConfig = {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        };
        const admins = await fetchAllPaginatedItems({
          url: api_endpoint + `/user/users/role/${"Admin"}`,
          token,
          selectItems: (data) => data?.users ?? [],
          mode: "skip",
          pageSize: 200,
          headers: reqConfig.headers,
        });
        setAdmins(admins || []);
      } catch (error) {
        console.error("Failed to fetch Admins:", error);
        setAdmins([]);
      } finally {
        setLoadingAdmins(false);
      }
    };
    fetchPathways();
    fetchInstructors();
    fetchManagers();
    fetchAdmins();
  }, [open]);

  // Fetch associated pathway IDs
  const fetchCoursePathways = async () => {
    try {
      const token = await getToken();
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };
      const res = await axios.get(
        api_endpoint + `/course/pathways/${course.id}`,
        config
      );
      // Pathways: [{id, title, ...}]
      setAssociatedPathwayIds((res.data || []).map((p) => String(p.id)));
    } catch {
      setAssociatedPathwayIds([]);
    }
  };

  // Fetch associated instructor IDs
  const fetchCourseInstructors = async () => {
    try {
      const token = await getToken();
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };
      const res = await axios.get(
        api_endpoint + `/course/${course.id}/instructors`,
        config
      );
      // Instructors: [{id, first_name, last_name, ...}]
      console.log("Associated instructors response:", res.data.instructors);
      setAssociatedInstructorIds(
        (res.data.instructors || []).map((i) => String(i.instructor_id))
      );
    } catch {
      setAssociatedInstructorIds([]);
    }
  };

  const fetchCourseManagers = async () => {
    try {
      const token = await getToken();
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };
      const res = await axios.get(
        api_endpoint + `/course/${course.id}/managers`,
        config
      );
      // Managers: [{id, first_name, last_name, ...}]
      console.log("Associated managers response:", res.data.managers);
      setAssociatedManagerIds(
        (res.data.managers || []).map((m) => String(m.manager_id))
      );
    } catch (error) {
      console.error("Failed to fetch Managers:", error);
      setAssociatedManagerIds([]);
    }
  };

  useEffect(() => {
    if (!open || !course?.id) return;
    fetchCoursePathways();
    fetchCourseInstructors();
    fetchCourseManagers();
  }, [open, course?.id]);

  const form = useForm({
    resolver: zodResolver(editCourseSchema),
    defaultValues: {
      title: course?.title ?? "",
      price: course?.price ?? "",
      slug: course?.slug ?? "",
      summary: course?.summary ?? "",
      thumbnail: course?.thumbnail ?? "",
      description: course?.description ?? "",
      pathways: course?.pathway_ids?.map((id) => String(id)) ?? [],
      instructors: course?.instructor_ids?.map((id) => String(id)) ?? [],
      managers: course?.manager_ids?.map((id) => String(id)) ?? [],
      admins: course?.admin_ids?.map((id) => String(id)) ?? [],
      difficulty: DIFFICULTY_REVERSE_MAP[course?.difficulty] ?? "Beginner",
      status: STATUS_REVERSE_MAP[course?.status] ?? "Draft",
      isOpen: course?.isOpen !== undefined ? String(course.isOpen) : "0",
    },
  });

  // Reset form when course changes
  useEffect(() => {
    if (open && course) {
      form.reset({
        title: course.title ?? "",
        price: course.price?.toString() ?? "",
        slug: course.slug ?? "",
        summary: course.summary ?? "",
        thumbnail: course.thumbnail ?? "",
        description: course.description ?? "",
        pathways:
          associatedPathwayIds.length > 0
            ? associatedPathwayIds
            : course.pathway_ids?.map((id) => String(id)) ?? [],
        instructors:
          associatedInstructorIds.length > 0
            ? associatedInstructorIds
            : course.instructor_ids?.map((id) => String(id)) ?? [],
        managers:
          associatedManagerIds.length > 0
            ? associatedManagerIds
            : course.manager_ids?.map((id) => String(id)) ?? [],
        admins: course.admin_ids?.map((id) => String(id)) ?? [],
        difficulty: DIFFICULTY_REVERSE_MAP[course.difficulty] ?? "Beginner",
        status: STATUS_REVERSE_MAP[course.status] ?? "Draft",
        isOpen: course.isOpen !== undefined ? String(course.isOpen) : "0",
      });
    }
  }, [
    open,
    course,
    associatedPathwayIds,
    associatedInstructorIds,
    associatedManagerIds,
    form,
  ]);

  const { getToken } = useAuth();
  const { user } = useUser();
  const { uploadFile } = useR2Upload();
  const onSubmit = async (values) => {
    try {
      const token = await getToken();
      if (!token) return;

      let thumbnailUrl = values.thumbnail || "";
      if (thumbnailFile) {
        try {
          const rawSlug = String(values.slug || "untitled").trim();
          const safeSlug = rawSlug
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-+|-+$/g, "");

          const ext = String(thumbnailFile.name || "")
            .split(".")
            .pop()
            ?.toLowerCase();
          const fileExt = ext && /^[a-z0-9]+$/.test(ext) ? ext : "jpg";

          const objectKey = `assets/thumbnails/courses/courses-${
            safeSlug || "untitled"
          }/thumbnail.${fileExt}`;

          const internalBucketName =
            import.meta.env.VITE_R2_INTERNAL_BUCKET_NAME ||
            import.meta.env.VITE_BUCKET_NAME;
          const internalPublicUrl =
            import.meta.env.VITE_R2_INTERNAL_PUBLIC_URL ||
            import.meta.env.VITE_R2_PUBLIC_URL;

          const uploadedUrl = await uploadFile(thumbnailFile, objectKey, {
            silent: true,
            bucketName: internalBucketName,
            publicUrlBase: internalPublicUrl,
            cacheBust: true,
          });

          if (!uploadedUrl) {
            toast.error("Thumbnail upload failed. Please try again.");
            return;
          }

          thumbnailUrl = uploadedUrl;
        } catch (uploadErr) {
          console.error("Thumbnail upload error:", uploadErr);
          toast.error("Thumbnail upload failed. Please try again.");
          return;
        }
      }

      // Map difficulty and status to numeric value
      const DIFFICULTY_MAP = {
        Beginner: 1,
        Intermediate: 2,
        Advanced: 3,
      };

      const STATUS_MAP = {
        Draft: 0,
        Published: 1,
      };

      // Filter each manager role to avoid duplicates
      const existingManagerIds =
        course.manager_ids?.map((id) => String(id)) ?? [];
      const newManagerIds = values.managers.filter(
        (id) => !existingManagerIds.includes(id)
      );
      const mergedManagers = [...existingManagerIds, ...newManagerIds];

      // Filter each instructor role to avoid duplicates
      const existingInstructorIds =
        course.instructor_ids?.map((id) => String(id)) ?? [];
      const newInstructorIds = values.instructors.filter(
        (id) => !existingInstructorIds.includes(id)
      );
      const mergedInstructors = [...existingInstructorIds, ...newInstructorIds];

      // Filter each pathway role to avoid duplicates
      const existingPathwayIds =
        course.pathway_ids?.map((id) => String(id)) ?? [];
      const newPathwayIds = values.pathways.filter(
        (id) => !existingPathwayIds.includes(id)
      );
      const mergedPathways = [...existingPathwayIds, ...newPathwayIds];

      const payload = {
        ...values,
        thumbnail: thumbnailUrl,
        price: parseFloat(values.price.replace(/[^\d.]/g, "")),
        difficulty: DIFFICULTY_MAP[values.difficulty],
        status: STATUS_MAP[values.status],
        pathways:
          values.pathways && values.pathways.length > 0
            ? [
                ...new Set([
                  ...(course.pathway_ids?.map((id) => String(id)) ?? []),
                  ...values.pathways,
                ]),
              ]
            : course.pathway_ids?.map((id) => String(id)) ?? [],
        instructors:
          values.instructors && values.instructors.length > 0
            ? [
                ...new Set([
                  ...(course.instructor_ids?.map((id) => String(id)) ?? []),
                  ...values.instructors,
                ]),
              ]
            : course.instructor_ids?.map((id) => String(id)) ?? [],
        managers:
          values.managers && values.managers.length > 0
            ? [
                ...new Set([
                  ...(course.manager_ids?.map((id) => String(id)) ?? []),
                  ...values.managers,
                ]),
              ]
            : course.manager_ids?.map((id) => String(id)) ?? [],
        admins:
          values.admins && values.admins.length > 0
            ? [
                ...new Set([
                  ...(course.admin_ids?.map((id) => String(id)) ?? []),
                  ...values.admins,
                ]),
              ]
            : course.admin_ids?.map((id) => String(id)) ?? [],
        isOpen: Number(values.isOpen),
      };

      console.log("Submitting course edit payload:", payload);

      // Make API call to update course
      const reqConfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      const response = await axios.put(
        api_endpoint + `/course/edit/${course.id}`,
        payload,
        reqConfig
      );
      toast.success(`Updated "${values.title}" successfully!`);
      setOpen(false);
      setThumbnailFile(null);
      setThumbnailPreview(null);

      // Call the callback function to notify parent component of update
      if (onCourseUpdated) {
        onCourseUpdated(response.data.course);
      }
      // Increment refreshKey to trigger OverviewTab reload
      if (setRefreshKey) {
        setRefreshKey((prevKey) => prevKey + 1);
      }
    } catch (error) {
      toast.error("Failed to update course.");
      console.error(error);
    }
  };

  const instructorMap = useMemo(() => {
    const map = {};
    instructors.forEach(
      (i) => (map[String(i.id)] = `${i.first_name} ${i.last_name}`)
    );

    const currentUserId = String(user?.id || "").trim();
    if (currentUserId && !map[currentUserId]) {
      const label =
        `${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
        user?.primaryEmailAddress?.emailAddress ||
        currentUserId;
      map[currentUserId] = label;
    }

    return map;
  }, [instructors, user?.id, user?.firstName, user?.lastName, user?.primaryEmailAddress?.emailAddress]);

  const managerMap = useMemo(() => {
    const map = {};
    managers.forEach(
      (m) => (map[String(m.id)] = `${m.first_name} ${m.last_name}`)
    );
    return map;
  }, [managers]);

  const pathwayMap = useMemo(() => {
    const map = {};
    pathways.forEach((p) => (map[String(p.id)] = p.title));
    return map;
  }, [pathways]);

  // Only close the markdown editor, not the main dialog
  const handleMarkdownSave = (newContent) => {
    form.setValue("description", newContent);
    setMarkdownEditorOpen(false);
  };

  const handleMarkdownEditorClose = () => {
    setMarkdownEditorOpen(false);
  };

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setThumbnailFile(file);
      const previewUrl = URL.createObjectURL(file);
      setThumbnailPreview(previewUrl);
      // update RHF form value so validation doesn't fail (we'll replace with uploaded URL when upload completes)
      form.setValue("thumbnail", previewUrl);
    } else {
      setThumbnailFile(null);
      setThumbnailPreview(null);
      form.setValue("thumbnail", "");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="" className={`${className || ""} `}>
          Edit Course
          <Pencil className="w-4 h-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="h-auto w-[calc(100vw-2rem)] max-w-3xl sm:w-150 md:w-175 lg:w-225 px-2 sm:px-8 overflow-x-hidden">
        <DialogHeader className="">
          <DialogTitle className="font-semibold font-heading">
            Edit Course
          </DialogTitle>
          <DialogDescription className="font-body">
            Make changes to your course and save.
          </DialogDescription>
        </DialogHeader>

        <div className="w-full max-w-full overflow-x-hidden">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-4 w-full max-w-full overflow-x-hidden"
              id="edit-course-form"
            >
              <div className="w-full">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field, fieldState }) => (
                    <Field>
                      <FieldLabel>Course Title *</FieldLabel>
                      <FieldContent>
                        <Input
                          {...field}
                          className={cn(
                            "mt-1",
                            fieldState.error &&
                              "border-(--color-destructive) focus-visible:ring-(--color-primary)"
                          )}
                        />
                        <FieldError
                          errors={
                            fieldState.error
                              ? [{ message: "This is required" }]
                              : []
                          }
                        />
                      </FieldContent>
                    </Field>
                  )}
                />
              </div>

              <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8">
                <FormField
                  control={form.control}
                  name="price"
                  render={({ field, fieldState }) => (
                    <Field>
                      <FieldLabel>Price *</FieldLabel>
                      <FieldContent>
                        <Input
                          {...field}
                          value={formatPrice(field.value)}
                          onChange={(e) => {
                            const rawValue = parsePrice(e.target.value);
                            field.onChange(rawValue);
                          }}
                          className={cn(
                            "mt-1",
                            fieldState.error &&
                              "border-(--color-destructive) focus-visible:ring-(--color-primary)"
                          )}
                        />
                        <FieldError
                          errors={
                            fieldState.error
                              ? [{ message: "This is required" }]
                              : []
                          }
                        />
                      </FieldContent>
                    </Field>
                  )}
                />

                <FormField
                  control={form.control}
                  name="slug"
                  render={({ field, fieldState }) => (
                    <Field>
                      <FieldLabel>Course Slug*</FieldLabel>
                      <FieldContent>
                        <Input
                          {...field}
                          placeholder="Python"
                          className={cn(
                            "mt-1",
                            fieldState.error &&
                              "border-(--color-destructive) focus-visible:ring-(--color-primary)"
                          )}
                        />
                        <p className="mt-1 text-xs text-(--neutral-500) font-body">
                       <Info className="w-3 h-3 inline-block mr-1" /> Course slugs are unique identifiers of a course. <span className="font-bold text-(--color-destructive)">Do not
                        include spaces.</span>
                      </p>
                        <FieldError
                          errors={
                            fieldState.error
                              ? [{ message: fieldState.error.message || "This is required" }]
                              : []
                          }
                        />
                      </FieldContent>
                    </Field>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="summary"
                render={({ field, fieldState }) => {
                  const wordCount =
                    typeof field.value === "string" && field.value.trim()
                      ? field.value.trim().split(/\s+/).length
                      : 0;
                  return (
                    <Field>
                      <FieldLabel>Summary *</FieldLabel>
                      <FieldContent>
                        <div>
                          <Textarea
                            {...field}
                            placeholder="Brief summary for course overview"
                            rows={2}
                            className={cn(
                              "mt-1",
                              fieldState.error &&
                                "border-(--color-destructive) focus-visible:ring-(--color-primary)"
                            )}
                          />
                          <div
                            className={`text-xs mt-1 ${
                              wordCount > 50
                                ? "text-(--color-primary)"
                                : "text-(--neutral-600)"
                            }`}
                          >
                            {wordCount} / 50 words
                          </div>
                        </div>
                        <FieldError
                          errors={
                            fieldState.error
                              ? [{ message: "This is required" }]
                              : []
                          }
                        />
                      </FieldContent>
                    </Field>
                  );
                }}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field, fieldState }) => {
                  return (
                    <Field>
                      <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <FieldLabel>
                          Description *
                          <div className="flex items-center gap-1 text-xs text-(--neutral-600) font-body">
                            <Info className="w-3 h-3" />
                            Markdown supported
                          </div>
                        </FieldLabel>
                        <Button
                          type="button"
                          onClick={() => setMarkdownEditorOpen(true)}
                          size=""
                          variant="outline"
                          className="w-full shrink-0 gap-2 sm:w-auto"
                        >
                          Edit in Fullscreen
                        </Button>
                      </div>
                      <FieldContent>
                        <div className="w-full min-w-0 max-w-full">
                          <Textarea
                            {...field}
                            rows={8}
                            className={cn(
                              "textarea-standard mt-1 w-full max-w-full",
                              fieldState.error &&
                                "border-(--color-destructive) focus-visible:ring-(--color-primary)"
                            )}
                          />
                        </div>
                        <FieldError
                          errors={
                            fieldState.error
                              ? [{ message: "This is required" }]
                              : []
                          }
                        />
                      </FieldContent>
                    </Field>
                  );
                }}
              />

              <FormField
                control={form.control}
                name="thumbnail"
                render={({ field }) => (
                  <Field>
                    <FieldLabel>
                      Course Thumbnail
                      <div className="text-xs text-(--neutral-600) font-body">
                        Need icons? Get them from{" "}
                        <a
                          href="https://undraw.co/"
                          target="_blank"
                          rel="noreferrer"
                          className="underline underline-offset-2 text-(--color-primary) font-body"
                        >
                          unDraw
                        </a>
                        
                      </div>
                    </FieldLabel>
                    <FieldContent>
                      {thumbnailPreview || (field.value && !thumbnailFile) ? (
                        <div className="relative w-full h-48 rounded-lg overflow-hidden border">
                          <img
                            src={thumbnailPreview || field.value}
                            alt="Thumbnail preview"
                            className="w-full h-full object-cover"
                          />
                          <Button
                            type="button"
                            variant="destructive"
                            size="icon"
                            className="absolute top-2 right-2 h-8 w-8"
                            onClick={() => {
                              setThumbnailFile(null);
                              setThumbnailPreview(null);
                              form.setValue("thumbnail", "");
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed rounded-lg cursor-pointer bg-(--neutral-50) hover:bg-(--neutral-100)">
                          <div className="flex flex-col items-center justify-center pt-5 pb-6">
                            <UploadCloud className="w-8 h-8 mb-4 text-(--neutral-500)" />
                            <p className="mb-2 text-sm text-(--neutral-500)"><span className="font-semibold">Click to upload</span> or drag and drop</p>
                            <p className="text-xs text-(--neutral-500)">SVG, PNG, JPG or GIF</p>
                          </div>
                          <Input type="file" accept="image/*" onChange={handleThumbnailChange} className="hidden" />
                        </label>
                      )}
                      <FieldError errors={[]} />
                    </FieldContent>
                  </Field>
                )}
              />

              <FormField
                control={form.control}
                name="pathways"
                render={({ field, fieldState }) => (
                  <Field>
                    <FieldLabel>
                      Associated Pathways
                      <div className="flex items-center gap-1 text-xs text-(--neutral-500) font-body">
                        <Info className="w-3 h-3" />
                        Optional
                      </div>
                    </FieldLabel>
                    <FieldContent>
                      <Select
                        onValueChange={(value) => {
                          if (value && !field.value.includes(value)) {
                            field.onChange([...field.value, value]);
                          }
                        }}
                        value=""
                        disabled={isLoadingPathways}
                      >
                        <SelectTrigger
                          className={cn(
                            "select-trigger mt-1 h-10 w-full font-body",
                            fieldState.error &&
                              "border-(--color-destructive) focus:ring-(--color-primary)"
                          )}
                        >
                          <SelectValue
                            placeholder={
                              isLoadingPathways
                                ? "Loading pathways..."
                                : "Select all that apply"
                            }
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {pathways
                            .filter(
                              (pathway) =>
                                !field.value.includes(String(pathway.id))
                            )
                            .map((pathway) => (
                              <SelectItem
                                key={pathway.id}
                                value={String(pathway.id)}
                                className="select-item"
                              >
                                {pathway.title}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      {field.value.length > 0 &&
                        (!allUserDataLoaded ? (
                          <div className="flex items-center gap-2 mt-2">
                            <ClipLoader size={16} color="var(--color-primary)" />
                            <span className="text-sm text-(--neutral-500) font-body">
                              Loading users...
                            </span>
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {field.value.map((pathwayId) => {
                              return (
                                <div key={pathwayId} className="tag-chip">
                                  <span className="font-body">
                                    {pathwayMap[String(pathwayId)] || pathwayId}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      field.onChange(
                                        field.value.filter((p) => p !== pathwayId)
                                      );
                                    }}
                                    className="chip-remove"
                                  >
                                    ×
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      <FieldError errors={[]} />
                    </FieldContent>
                  </Field>
                )}
              />

              <FormField
                control={form.control}
                name="instructors"
                render={({ field, fieldState }) => (
                  <Field>
                    <FieldLabel>
                      Instructors
                      <div className="flex items-center gap-1 text-xs text-(--neutral-500) font-body">
                        <Info className="w-3 h-3" />
                        Optional
                      </div>
                    </FieldLabel>
                    <FieldContent>
                      <Select
                        onValueChange={(value) => {
                          if (value && !field.value.includes(value)) {
                            field.onChange([...field.value, value]);
                          }
                        }}
                        value=""
                        disabled={loadingInstructors}
                      >
                        <SelectTrigger
                          className={cn(
                            "select-trigger mt-1 h-10 w-full font-body",
                            fieldState.error &&
                              "border-(--color-destructive) focus:ring-(--color-primary)"
                          )}
                        >
                          <SelectValue
                            placeholder={
                              loadingInstructors
                                ? "Loading instructors..."
                                : "Select all that apply"
                            }
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {instructors
                            .filter(
                              (instructor) =>
                                !field.value.map(String).includes(String(instructor.id))
                            )
                            .map((instructor) => (
                              <SelectItem
                                key={instructor.id}
                                value={String(instructor.id)}
                                className="select-item"
                              >
                                {instructor.first_name} {instructor.last_name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      {field.value.length > 0 &&
                        (!allUserDataLoaded ? (
                          <div className="flex items-center gap-2 mt-2">
                            <ClipLoader size={16} color="var(--color-primary)" />
                            <span className="text-sm text-(--neutral-500) font-body">
                              Loading users...
                            </span>
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {field.value.map((instructorId) => {
                              return (
                                <div key={instructorId} className="tag-chip">
                                  <span className="font-body">
                                    {instructorMap[String(instructorId)] ||
                                      instructorId}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      field.onChange(
                                        field.value.filter((id) => id !== instructorId)
                                      );
                                    }}
                                    className="chip-remove"
                                  >
                                    ×
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      <FieldError errors={[]} />
                    </FieldContent>
                  </Field>
                )}
              />

              

              <FormField
                control={form.control}
                name="managers"
                render={({ field, fieldState }) => (
                  <Field>
                    <FieldLabel>
                      Managers
                      <div className="flex items-center gap-1 text-xs text-(--neutral-500) font-body">
                        <Info className="w-3 h-3" />
                        Optional
                      </div>
                    </FieldLabel>
                    <FieldContent>
                      <Select
                        onValueChange={(value) => {
                          if (value && !field.value.includes(value)) {
                            field.onChange([...field.value, value]);
                          }
                        }}
                        value=""
                        disabled={loadingManagers}
                      >
                        <SelectTrigger
                          className={cn(
                            "select-trigger mt-1 h-10 w-full font-body",
                            fieldState.error &&
                              "border-(--color-destructive) focus:ring-(--color-destructive)"
                          )}
                        >
                          <SelectValue
                            placeholder={
                              loadingManagers
                                ? "Loading managers..."
                                : "Select all that apply"
                            }
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {managers
                            .filter(
                              (manager) => !field.value.includes(manager.id)
                            )
                            .map((manager) => (
                              <SelectItem
                                key={manager.id}
                                value={manager.id}
                                className="select-item"
                              >
                                {manager.first_name} {manager.last_name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      {field.value.length > 0 &&
                        (!allUserDataLoaded ? (
                          <div className="flex items-center gap-2 mt-2">
                            <ClipLoader size={16} color="var(--color-primary)" />
                            <span className="text-sm text-(--neutral-500) font-body">
                              Loading users...
                            </span>
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {field.value.map((managerId) => {
                              return (
                                <div key={managerId} className="tag-chip">
                                  <span className="font-body">
                                    {managerMap[String(managerId)] || managerId}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      field.onChange(
                                        field.value.filter((id) => id !== managerId)
                                      );
                                    }}
                                    className="chip-remove"
                                  >
                                    ×
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      <FieldError errors={[]} />
                    </FieldContent>
                  </Field>
                )}
              />

              {/* <FormField
                                control={form.control}
                                name="admins"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="text-sm font-medium text-black font-heading">
                                            Admins
                                            <div className="flex items-center gap-1 text-xs text-gray-500 font-body">
                                                <Info className="w-3 h-3" />
                                                Optional
                                            </div>
                                        </FormLabel>
                                        <FormControl>
                                            <Select
                                                onValueChange={(value) => {
                                                    if (value && !field.value.includes(value)) {
                                                        field.onChange([...field.value, value]);
                                                    }
                                                }}
                                                value=""
                                                disabled={loadingAdmins}
                                            >
                                                <SelectTrigger
                                                    className="select-trigger mt-1 h-10 w-full font-body">
                                                    <SelectValue
                                                        placeholder={
                                                            loadingAdmins
                                                                ? "Loading admins..."
                                                                : "Select all that apply"
                                                        }
                                                    />
                                                </SelectTrigger>
                                                <SelectContent className="select-content">
                                                    {admins.map((admin) => (
                                                        <SelectItem
                                                            key={admin.id}
                                                            value={admin.id}
                                                            className="select-item font-body"
                                                        >
                                                            {admin.first_name} {admin.last_name}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </FormControl>
                                        {field.value.length > 0 && (
                                            <div className="flex flex-wrap gap-2 mt-2">
                                                {field.value.map((adminId) => {
                                                    const admin = admins.find((a) => a.id === adminId);
                                                    return (
                                                        <div
                                                            key={adminId}
                                                            className="tag-chip"
                                                        >
                                                            <span className="font-body">
                                                                {admin
                                                                    ? `${admin.first_name} ${admin.last_name}`
                                                                    : adminId}
                                                            </span>
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    field.onChange(
                                                                        field.value.filter((id) => id !== adminId)
                                                                    );
                                                                }}
                                                                className="chip-remove"
                                                            >
                                                                ×
                                                            </button>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                        <FormMessage />
                                    </FormItem>
                                )}
                            /> */}

              <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8">
                <FormField
                  control={form.control}
                  name="difficulty"
                  render={({ field, fieldState }) => (
                    <Field>
                      <FieldLabel>Difficulty *</FieldLabel>
                      <FieldContent>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <SelectTrigger
                            className={cn(
                              "select-trigger h-12 mt-1 w-full font-body",
                              fieldState.error &&
                                "border-(--color-destructive) focus:ring-(--color-destructive)"
                            )}
                          >
                            <SelectValue placeholder="Select Level" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Beginner">Beginner</SelectItem>
                            <SelectItem value="Intermediate">
                              Intermediate
                            </SelectItem>
                            <SelectItem value="Advanced">Advanced</SelectItem>
                          </SelectContent>
                        </Select>
                        <FieldError
                          errors={
                            fieldState.error
                              ? [{ message: "This is required" }]
                              : []
                          }
                        />
                      </FieldContent>
                    </Field>
                  )}
                />
                {/* <FormField
                                    control={form.control}
                                    name="status"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="text-sm font-medium text-(--neutral-900) font-heading">Status</FormLabel>
                                            <FormControl>
                                                <Select onValueChange={field.onChange} value={field.value} disabled>
                                                    <SelectTrigger className="select-trigger h-12 mt-1 w-full font-body" data-focus-red>
                                                        <SelectValue placeholder="Select Level" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="Draft">Draft</SelectItem>
                                                        <SelectItem value="Published">Published</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                /> */}

                <FormField
                  control={form.control}
                  name="isOpen"
                  className="w-full"
                  render={({ field, fieldState }) => (
                    <Field>
                      <FieldLabel>Course Visibility *</FieldLabel>
                      <FieldContent>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <SelectTrigger
                            className={cn(
                              "select-trigger mt-1 h-10 w-full font-body",
                              fieldState.error &&
                                "border-(--color-destructive) focus:ring-(--color-primary)"
                            )}
                            data-focus-red
                          >
                            <SelectValue placeholder="Select visibility" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1">Open</SelectItem>
                            <SelectItem value="0">Closed</SelectItem>
                          </SelectContent>
                        </Select>

                        <FieldError
                          errors={
                            fieldState.error
                              ? [{ message: "This is required" }]
                              : []
                          }
                        />
                      </FieldContent>
                    </Field>
                  )}
                />
              </div>
            </form>
          </Form>
          <DialogFooter className="mt-4">
            <DialogClose asChild>
              <Button
                type="button"
                variant="outline"
                className=""
                disabled={form.formState.isSubmitting}
              >
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="submit"
              form="edit-course-form"
              className=""
              variant=""
              disabled={form.formState.isSubmitting}
            >
              {form.formState.isSubmitting ? (
                <ClipLoader size={20} color="var(--color-primary)" />
              ) : (
                "Save Changes"
              )}
            </Button>
          </DialogFooter>
        </div>
        <div className="w-full h-full">
          <MarkdownEditorDialog
            open={markdownEditorOpen}
            initialValue={form.watch("description")}
            onClose={handleMarkdownEditorClose}
            onSave={handleMarkdownSave}
            contentFolder={"courses"}
            contentName={course?.slug}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
